import constants from '@/constants';

import assign from 'lodash/assign';
import map from 'lodash/map';
import flatMap from 'lodash/flatMap';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import times from 'lodash/times';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import first from 'lodash/first';
import forEach from 'lodash/forEach';
import last from 'lodash/last';
import some from 'lodash/some';
import findIndex from 'lodash/findIndex';
import findLast from 'lodash/findLast';
import extend from 'lodash/extend';
import constant from 'lodash/constant';

import differenceInMinutes from 'date-fns/differenceInMinutes';
import startOfDay from 'date-fns/startOfDay';
import addDays from 'date-fns/addDays';
import isAfter from 'date-fns/isAfter';
import areIntervalsOverlapping from 'date-fns/areIntervalsOverlapping';
import format from 'date-fns/format';
import isBefore from 'date-fns/isBefore';
import addMinutes from 'date-fns/addMinutes';
import differenceInDays from 'date-fns/differenceInDays';
import differenceInHours from 'date-fns/differenceInHours';

import getActualStartOrEndTime from '@/mixins/timepairs/getActualStartOrEndTime';
import LocalDate from 'lodash';

const WorkScheduleUtil = {

  YEAR_MONTH_DAY_FORMAT: 'yyyy-MM-dd\'T\'',

  HOUR_MINUTE_FORMAT: 'HH:mm',

  HOURS_MINUTES_SECONDS_FORMAT: 'HH:mm:ss',

  getTimePairWithWorkScheduleData(state, timePair) {
    if (timePair.startingWorkScheduleDay == null) {
      return timePair;
    }

    const actualSpanningDays = this.getSpanningDays(timePair);
    const expectedSpanningDays = this.getExpectedSpanningDays(state, timePair, actualSpanningDays);

    const expectedDuration = this.getWorkScheduleExpectedDuration(
      state,
      actualSpanningDays,
      timePair,
    );

    if (timePair.adjustedVersion != null) {
      timePair.adjustedVersion = assign(
        {},
        timePair.adjustedVersion,
        {
          actualDuration: timePair.adjustedVersion.duration,
          spanningDays: expectedSpanningDays,
          expectedMinuteDuration: expectedDuration,
          workSchedule: this.getWorkScheduleDaySegments(state, timePair.adjustedVersion, actualSpanningDays,
            expectedSpanningDays),
          workScheduleMinuteDeviation: timePair.adjustedVersion.roundedDuration - expectedDuration,
        },
      );
    }

    return assign(
      {},
      timePair,
      {
        actualDuration: timePair.duration,
        spanningDays: expectedSpanningDays,
        expectedMinuteDuration: expectedDuration,
        workSchedule: this.getWorkScheduleDaySegments(state, timePair, actualSpanningDays, expectedSpanningDays),
        workScheduleMinuteDeviation: (timePair.absenceType == null) ? timePair.roundedDuration - expectedDuration
          : 0,
      },
    );
  },

  getWorkScheduleDaySegments(state, timePair, actualSpanningDays, expectedSpanningDays) {
    const expectedDays = map(this.getExpectedWorkScheduleDays(
      state,
      timePair.startingWorkScheduleDay,
      actualSpanningDays,
      timePair.workSchedule,
    ), (day) => day.segments);

    const actualStartTime = new Date(getActualStartOrEndTime.methods.getActualStartTime(timePair).date);

    const actualEndTime = timePair.clockOut != null
      ? new Date(getActualStartOrEndTime.methods.getActualEndTime(timePair).durationAccurateDate)
      : addMinutes(new Date(), ((new Date()).getTimezoneOffset() - timePair.clockIn.timeZoneOffset));

    const startOfStartingDay = startOfDay(actualStartTime);

    let endOfEndingDay = startOfDay(addDays(actualEndTime, 1));
    if (expectedSpanningDays > actualSpanningDays) {
      endOfEndingDay = addDays(endOfEndingDay, expectedSpanningDays - actualSpanningDays);
    }

    return {
      expected: this.getPaddedExpectedSegments(
        timePair,
        expectedDays,
        actualStartTime,
        actualEndTime,
        startOfStartingDay,
        endOfEndingDay,
        expectedSpanningDays,
      ),
      actual: this.getActualSegments(
        timePair,
        actualStartTime,
        actualEndTime,
        startOfStartingDay,
        endOfEndingDay,
        expectedSpanningDays,
        timePair.clockOut != null,
      ),
    };
  },

  getWorkScheduleExpectedDuration(state, spanningDays, timePair) {
    let expectedWorkScheduleDays = map(this.getExpectedWorkScheduleDays(
      state,
      timePair.startingWorkScheduleDay,
      spanningDays,
      timePair.workSchedule,
    ));

    let expectedDuration = 0;
    const clockOut = timePair.clockOut ? timePair.clockOut.date : LocalDate.now();
    let difference = differenceInHours(new Date(clockOut), new Date(timePair.clockIn.date));

    expectedWorkScheduleDays = expectedWorkScheduleDays.filter((day) => day.workingMinutes > 0);
    expectedWorkScheduleDays.forEach((day) => {
      if (timePair.workSchedule.timeRecordingVariant === constants.WorkSchedule.TimeRecordingVariant.DURATION) {
        expectedDuration += day.workingMinutes;
      } else {
        const segments = filter(day.segments,
          (segment) => segment.category === constants.WorkSchedule.SegmentType.SCHEDULED_WORKING_TIME);

        segments.forEach((segment) => {
          if (difference > 0) {
            expectedDuration += segment.minuteDuration;
          }
        });
      }

      difference -= 24;
    });

    return expectedDuration;
  },

  getExpectedWorkScheduleDays(state, startingDayNumber, spanningDays, workSchedule) {
    const totalWorkScheduleDays = workSchedule.days.length;
    const expectedDays = [];

    let dayNumberIterator = startingDayNumber;

    times(spanningDays, () => {
      expectedDays.push(cloneDeep(find(
        workSchedule.days,
        (workScheduleDay) => workScheduleDay.day === dayNumberIterator,
      )));

      if (dayNumberIterator === totalWorkScheduleDays) {
        dayNumberIterator = 1;
      } else {
        dayNumberIterator++;
      }
    });

    return expectedDays;
  },

  getActualSegments(
    timePair,
    actualStartTime,
    actualEndTime,
    startOfStartingDay,
    endOfEndingDay,
    spanningDays,
    isTimePairComplete,
  ) {
    let actualSegments = [];
    if (isAfter(actualStartTime, startOfStartingDay)) {
      actualSegments.push(this.getPlaceholderSegmentWithTime(startOfStartingDay, actualStartTime));
    }

    const nonOtDeletedSegments = filter(timePair.segmentTimePairs,
      (segment) => !((segment.pairType === constants.WorkSchedule.SegmentType.OT_BREAK
        || segment.pairType === constants.WorkSchedule.SegmentType.OVERTIME
        || segment.pairType === constants.WorkSchedule.SegmentType.EARLY_LOGIN
        || segment.pairType === constants.WorkSchedule.SegmentType.PRE_SHIFT_OT
        || segment.pairType === constants.WorkSchedule.SegmentType.FIXED_OVERTIME) && segment.deletedDate != null));

    const segments = nonOtDeletedSegments.sort((a, b) => {
      if (+new Date(a.clockIn.date) === +new Date(b.clockIn.date)) {
        return new Date(b.clockOut.date) - new Date(a.clockOut.date);
      }
      return new Date(a.clockIn.date) - new Date(b.clockIn.date);
    });

    if (!isEmpty(segments)) {
      const middleSegments = this.getActualSlicesWithSegmentTimePairs(
        segments,
        actualStartTime,
        timePair.clockIn.timeZoneOffset,
        actualEndTime,
        timePair.clockOut ? timePair.clockOut.timeZoneOffset : timePair.clockIn.timeZoneOffset,
        isTimePairComplete,
        timePair.timeType,
      );
      actualSegments = actualSegments.concat(middleSegments);
    } else {
      actualSegments.push(this.getFinalSegment(
        actualStartTime,
        actualEndTime,
        isTimePairComplete,
        timePair.timeType,
      ));
    }

    if (isBefore(actualEndTime, endOfEndingDay)) {
      const lastSegmentEnd = last(actualSegments).endTime;
      if (isAfter(lastSegmentEnd, actualEndTime)) {
        actualSegments.push(this.getPlaceholderSegmentWithTime(lastSegmentEnd, endOfEndingDay));
      } else {
        actualSegments.push(this.getPlaceholderSegmentWithTime(actualEndTime, endOfEndingDay));
      }
    }

    forEach(actualSegments, (segment) => {
      segment.startTime = format(segment.startTime, this.HOUR_MINUTE_FORMAT);
      segment.endTime = format(segment.endTime, this.HOUR_MINUTE_FORMAT);
    });

    return this.getSegmentSummaries(
      actualSegments,
      actualStartTime,
      actualEndTime,
      spanningDays,
    );
  },

  getActualSlicesWithSegmentTimePairs(
    segmentTimePairs,
    actualStartTime,
    actualStartTimeOffset,
    actualEndTime,
    actualEndTimeOffset,
    isTimePairComplete,
    timeType,
  ) {
    const actualSegments = [];
    const firstSegment = first(segmentTimePairs);
    const firstSegmentStartingTime = new Date(firstSegment.clockIn.date);

    if (+actualStartTime !== +firstSegmentStartingTime) {
      if (isAfter(firstSegmentStartingTime, actualStartTime)) {
        actualSegments.push(this.getInitialWorkingSegment(
          actualStartTime,
          actualStartTimeOffset,
          firstSegmentStartingTime,
          firstSegment.clockIn.timeZoneOffset,
          timeType,
        ));
      }
    }

    forEach(segmentTimePairs, (workingSegment, i) => {
      const currentSegmentStartingTime = new Date(workingSegment.clockIn.date);
      const currentSegmentEndingTime = workingSegment.clockOut == null
        ? addMinutes(new Date(), ((new Date()).getTimezoneOffset() - workingSegment.clockIn.timeZoneOffset))
        : new Date(workingSegment.clockOut.durationAccurateDate);

      const ongoingSegment = workingSegment.clockOut == null;

      actualSegments.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
      const prevSegment = last(actualSegments);
      if (i > 0 && isAfter(prevSegment.endTime, currentSegmentStartingTime)
        && workingSegment.pairType !== constants.WorkSchedule.SegmentType.OT_BREAK
        && workingSegment.pairType !== constants.WorkSchedule.SegmentType.BREAK
      ) {
        actualSegments.push(this.getSegmentTimePairSlice(
          prevSegment.endTime,
          currentSegmentEndingTime,
          ongoingSegment,
          workingSegment.pairType,
        ));
      } else {
        actualSegments.push(this.getSegmentTimePairSlice(
          currentSegmentStartingTime,
          currentSegmentEndingTime,
          ongoingSegment,
          workingSegment.pairType,
        ));
      }

      const actualSegmentsLength = actualSegments.length;
      actualSegments.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
      const lastSegmentSoFar = last(actualSegments);
      const lastSegmentEndingTime = lastSegmentSoFar.endTime;
      const lastSegmentStartTime = lastSegmentSoFar.startTime;

      if (i < segmentTimePairs.length - 1) {
        const nextSegment = segmentTimePairs[i + 1];
        const nextSegmentStartingTime = new Date(nextSegment.clockIn.date);
        const nextSegmentEndingTime = new Date(nextSegment.clockOut.date);

        if (+lastSegmentEndingTime !== +nextSegmentStartingTime) {
          const range1 = { start: lastSegmentStartTime, end: lastSegmentEndingTime };
          const range2 = { start: nextSegmentStartingTime, end: nextSegmentEndingTime };
          if ((isAfter(nextSegmentStartingTime, lastSegmentStartTime)
            || +nextSegmentStartingTime === +lastSegmentStartTime) && areIntervalsOverlapping(range1, range2)) {
            if (lastSegmentSoFar.category !== constants.WorkSchedule.SegmentType.ACTUAL_BREAK_TIME
              && lastSegmentSoFar.category !== constants.WorkSchedule.SegmentType.OT_BREAK
              && lastSegmentSoFar.category !== constants.WorkSchedule.SegmentType.BREAK
            ) {
              if (isAfter(nextSegmentStartingTime, lastSegmentStartTime)) {
                actualSegments.push(this.getIntermediateSegment(
                  lastSegmentStartTime,
                  nextSegmentStartingTime,
                  lastSegmentSoFar.category,
                ));
              }
              if (isBefore(nextSegmentEndingTime, lastSegmentEndingTime)) {
                actualSegments.push(this.getIntermediateSegment(
                  nextSegmentEndingTime,
                  lastSegmentEndingTime,
                  lastSegmentSoFar.category,
                ));
              }
              actualSegments.splice(actualSegmentsLength - 1, 1);
            }
          } else {
            actualSegments.push(this.getIntermediateWorkingSegment(lastSegmentEndingTime, nextSegmentStartingTime));
          }
        }
      }
    });

    const orderedSegments = segmentTimePairs.sort((a, b) => new Date(a.clockOut.date) - new Date(b.clockOut.date));
    const lastSegment = last(orderedSegments);

    const finalSegmentEndingTime = last(actualSegments).endTime;

    if (+finalSegmentEndingTime !== +actualEndTime
      && (lastSegment.pairType === constants.WorkSchedule.SegmentType.BREAK
        || lastSegment.pairType === constants.WorkSchedule.SegmentType.OVERTIME
        || lastSegment.pairType === constants.WorkSchedule.SegmentType.EARLY_LOGIN
        || lastSegment.pairType === constants.WorkSchedule.SegmentType.PRE_SHIFT_OT
        || lastSegment.pairType === constants.WorkSchedule.SegmentType.OT_BREAK
      || lastSegment.pairType === constants.WorkSchedule.SegmentType.FIXED_OVERTIME)
    && !isAfter(finalSegmentEndingTime, actualEndTime)) {
      actualSegments.push(this.getFinalSegment(
        finalSegmentEndingTime,
        addMinutes(new Date(actualEndTime), (actualEndTimeOffset - lastSegment.clockIn.timeZoneOffset)),
        isTimePairComplete,
        timeType,
      ));
    }

    return actualSegments;
  },

  getPaddedExpectedSegments(
    timePair,
    expectedDays,
    actualStartTime,
    actualEndTime,
    startOfStartingDay,
    endOfEndingDay,
    spanningDays,
  ) {
    let segments = [];
    const firstWorkingDayIndex = findIndex(expectedDays, (expectedDaySegments) => expectedDaySegments.length > 0);
    const firstWorkingDay = expectedDays[firstWorkingDayIndex];

    if (!firstWorkingDay) {
      return this.getExpectedSegmentsForNonWorkingDay(startOfStartingDay, endOfEndingDay);
    }
    const firstWorkingDayStartTime = new Date(
      format(actualStartTime, this.YEAR_MONTH_DAY_FORMAT) + cloneDeep(first(firstWorkingDay).startTime),
    );
    if (firstWorkingDayIndex !== 0) {
      firstWorkingDayStartTime.setDate(firstWorkingDayStartTime.getDate() + firstWorkingDayIndex);
    }

    if (isAfter(firstWorkingDayStartTime, startOfStartingDay)) {
      segments.push(this.getPlaceholderSegment(startOfStartingDay, firstWorkingDayStartTime));
    }

    segments = segments.concat(this.getExpectedSegments(startOfStartingDay, actualStartTime, expectedDays, timePair));

    const lastSegment = last(segments);

    let lastEndingTime = new Date(
      format(actualEndTime, this.YEAR_MONTH_DAY_FORMAT) + cloneDeep(lastSegment.endTime),
    );

    if (this.segmentIsAcrossMidnight(lastSegment)) {
      lastEndingTime = addDays(lastEndingTime, 1);
    }

    if (isBefore(lastEndingTime, endOfEndingDay)) {
      segments.push(this.getPlaceholderSegment(lastEndingTime, endOfEndingDay));
    }

    return this.getSegmentSummaries(
      segments,
      firstWorkingDayStartTime,
      lastEndingTime,
      spanningDays,
    );
  },

  getExpectedSegments(startOfStartingDay, actualStartTime, expectedDays, timePair) {
    const segments = [];

    let dayIterator = startOfStartingDay;

    forEach(expectedDays, (expectedDaySegments, index) => {
      if (!isEmpty(expectedDaySegments)) {
        if (some(
          expectedDaySegments, (segment) => segment.category === constants.WorkSchedule.SegmentType.FLEXTIME_BANDWIDTH,
        )) {
          expectedDaySegments = this.getAdjustedFlexTimeSegments(expectedDaySegments, timePair, actualStartTime);
        }

        segments.push(...expectedDaySegments);

        if (index < expectedDays.length - 1) {
          const nextWorkingDayIndex = findIndex(
            expectedDays,
            (nextExpectedDaySegments) => nextExpectedDaySegments.length > 0,
            index + 1,
          );

          if (nextWorkingDayIndex !== -1) {
            const nextWorkingDay = expectedDays[nextWorkingDayIndex];
            const nextStartingTime = new Date(
              format(
                addDays(new Date(dayIterator), nextWorkingDayIndex - index),
                this.YEAR_MONTH_DAY_FORMAT,
              ) + cloneDeep(first(nextWorkingDay).startTime),
            );

            const lastSegment = last(expectedDaySegments);

            let currentDayEndingTime = new Date(format(dayIterator, this.YEAR_MONTH_DAY_FORMAT)
              + lastSegment.endTime);

            if (this.segmentIsAcrossMidnight(lastSegment)) {
              currentDayEndingTime = addDays(currentDayEndingTime, 1);
            }

            segments.push(this.getPlaceholderSegment(currentDayEndingTime, nextStartingTime));
          }
        }
      }
      dayIterator = addDays(new Date(dayIterator), 1);
    });

    return segments;
  },

  getPlaceholderSegment(startTime, endTime) {
    return {
      category: constants.WorkSchedule.SegmentType.PLACEHOLDER,
      minuteDuration: differenceInMinutes(endTime, startTime),
    };
  },

  getPlaceholderSegmentWithTime(startTime, endTime) {
    return {
      startTime,
      endTime,
      category: constants.WorkSchedule.SegmentType.PLACEHOLDER,
      minuteDuration: differenceInMinutes(endTime, startTime),
    };
  },

  getInitialWorkingSegment(
    actualStartTime,
    actualStartTimeOffset,
    firstBreakStartingTime,
    firstBreakStartingTimeOffset,
    timeType,
  ) {
    const timeZoneAdjustedEndTime = addMinutes(
      firstBreakStartingTime,
      (firstBreakStartingTimeOffset - actualStartTimeOffset),
    );

    return {
      startTime: actualStartTime,
      endTime: timeZoneAdjustedEndTime,
      minuteDuration: differenceInMinutes(timeZoneAdjustedEndTime, actualStartTime),
      category: (timeType === constants.WorkSchedule.TimeType.OVERTIME
      || timeType === constants.WorkSchedule.TimeType.FIXED_OVERTIME) ? constants.WorkSchedule.SegmentType.OVERTIME
        : constants.WorkSchedule.SegmentType.ACTUAL_WORKING_TIME,
    };
  },

  getSegmentTimePairSlice(breakStartTime, breakEndTime, ongoingSegment, pairType) {
    let segmentType;

    switch (pairType) {
      case constants.WorkSchedule.SegmentType.BREAK:
        segmentType = ongoingSegment ? constants.WorkSchedule.SegmentType.ONGOING_BREAK_TIME
          : constants.WorkSchedule.SegmentType.ACTUAL_BREAK_TIME;
        break;
      case constants.WorkSchedule.SegmentType.FIXED_OVERTIME:
        segmentType = ongoingSegment ? constants.WorkSchedule.SegmentType.ONGOING_OVERTIME
          : constants.WorkSchedule.SegmentType.FIXED_OVERTIME_WORKING_TIME;
        break;
      case constants.WorkSchedule.SegmentType.OVERTIME:
        segmentType = ongoingSegment ? constants.WorkSchedule.SegmentType.ONGOING_OVERTIME
          : constants.WorkSchedule.SegmentType.OVERTIME;
        break;
      case constants.WorkSchedule.SegmentType.EARLY_LOGIN:
        segmentType = constants.WorkSchedule.SegmentType.EARLY_LOGIN;
        break;
      case constants.WorkSchedule.SegmentType.PRE_SHIFT_OT:
        segmentType = constants.WorkSchedule.SegmentType.PRE_SHIFT_OT;
        break;
      case constants.WorkSchedule.SegmentType.OT_BREAK:
        segmentType = constants.WorkSchedule.SegmentType.OT_BREAK;
        break;
      default:
        segmentType = constants.WorkSchedule.SegmentType.UNKNOWN;
    }
    return {
      startTime: breakStartTime,
      endTime: breakEndTime,
      minuteDuration: differenceInMinutes(breakEndTime, breakStartTime),
      category: segmentType,
    };
  },

  getIntermediateWorkingSegment(startTime, endTime) {
    return {
      startTime,
      endTime,
      minuteDuration: differenceInMinutes(endTime, startTime),
      category: constants.WorkSchedule.SegmentType.ACTUAL_WORKING_TIME,
    };
  },

  getIntermediateSegment(startTime, endTime, category) {
    return {
      startTime,
      endTime,
      minuteDuration: differenceInMinutes(endTime, startTime),
      category,
    };
  },

  getFinalSegment(startTime, endTime, isTimePairComplete, timeType) {
    let category;

    switch (timeType) {
      case constants.WorkSchedule.TimeType.OVERTIME:
        category = isTimePairComplete ? constants.WorkSchedule.SegmentType.OVERTIME
          : constants.WorkSchedule.SegmentType.ONGOING_OVERTIME;
        break;
      case constants.WorkSchedule.TimeType.FIXED_OVERTIME:
        category = isTimePairComplete ? constants.WorkSchedule.SegmentType.FIXED_OVERTIME_WORKING_TIME
          : constants.WorkSchedule.SegmentType.ONGOING_OVERTIME;
        break;
      case constants.WorkSchedule.TimeType.EARLY_LOGIN:
        category = constants.WorkSchedule.SegmentType.EARLY_LOGIN;
        break;
      case constants.WorkSchedule.TimeType.PRE_SHIFT_OT:
        category = constants.WorkSchedule.SegmentType.PRE_SHIFT_OT;
        break;
      default:
        category = isTimePairComplete ? constants.WorkSchedule.SegmentType.ACTUAL_WORKING_TIME
          : constants.WorkSchedule.SegmentType.ONGOING_WORK_TIME;
    }
    return {
      startTime,
      endTime,
      minuteDuration: differenceInMinutes(endTime, startTime),
      category,
    };
  },

  getExpectedSegmentsForNonWorkingDay(startOfStartingDay, endOfEndingDay) {
    return {
      segments: [{
        category: constants.WorkSchedule.SegmentType.PLACEHOLDER,
        minuteDuration: differenceInMinutes(endOfEndingDay, startOfStartingDay),
        widthPercentage: 100,
      }],
      startTime: startOfStartingDay,
      endTime: endOfEndingDay,
    };
  },

  getAdjustedFlexTimeSegments(initialSegments, timePair, actualStartTime) {
    const datePortion = format(actualStartTime, this.YEAR_MONTH_DAY_FORMAT);

    const startingBoundaryTime = new Date(datePortion + first(initialSegments).startTime);

    const endingBoundaryTime = new Date(datePortion + last(initialSegments).endTime);

    const firstWorkingSegmentStartTime = this.getFirstWorkingSegmentStartTime(datePortion, initialSegments);

    const lastWorkingSegmentEndTime = this.getLastWorkingSegmentEndTime(datePortion, initialSegments);

    const flexTimeExpectedStartTimeOffset = differenceInMinutes(startingBoundaryTime, firstWorkingSegmentStartTime);

    const flexTimeExpectedEndTimeOffset = differenceInMinutes(endingBoundaryTime, lastWorkingSegmentEndTime);

    const offset = this.getFlextimeOffset(
      actualStartTime,
      startingBoundaryTime,
      endingBoundaryTime,
      flexTimeExpectedStartTimeOffset,
      flexTimeExpectedEndTimeOffset,
      firstWorkingSegmentStartTime,
    );

    const adjustedSegments = this.getAdjustedFlextimeBandwidths(initialSegments, datePortion, offset);

    const adjustedFirstWorkingSegmentStartTime = new Date(datePortion + first(adjustedSegments).startTime);

    const adjustedLastWorkingSegmentEndTime = new Date(datePortion + last(adjustedSegments).endTime);

    if (isAfter(adjustedFirstWorkingSegmentStartTime, startingBoundaryTime)) {
      adjustedSegments.unshift(
        this.getAdjustedLeadingFlextimeBandwidth(startingBoundaryTime, adjustedFirstWorkingSegmentStartTime),
      );
    }

    if (isBefore(adjustedLastWorkingSegmentEndTime, endingBoundaryTime)) {
      adjustedSegments.push(
        this.getAdjustedTrailingFlextimeBandwidth(adjustedLastWorkingSegmentEndTime, endingBoundaryTime),
      );
    }

    return adjustedSegments;
  },

  getFirstWorkingSegmentStartTime(datePortion, initialSegments) {
    return new Date(
      datePortion + find(
        initialSegments,
        (segment) => segment.category === constants.WorkSchedule.SegmentType.SCHEDULED_WORKING_TIME,
      ).startTime,
    );
  },

  getLastWorkingSegmentEndTime(datePortion, initialSegments) {
    return new Date(
      datePortion + findLast(
        initialSegments,
        (segment) => segment.category === constants.WorkSchedule.SegmentType.SCHEDULED_WORKING_TIME,
      ).endTime,
    );
  },

  getFlextimeOffset(
    actualStartTime,
    startingBoundaryTime,
    endingBoundaryTime,
    flexTimeExpectedStartTimeOffset,
    flexTimeExpectedEndTimeOffset,
    firstWorkingSegmentStartTime,
  ) {
    let offset;

    if (isBefore(actualStartTime, startingBoundaryTime)) {
      offset = -flexTimeExpectedStartTimeOffset;
    } else if (isBefore(actualStartTime, endingBoundaryTime)) {
      if (isBefore(actualStartTime, firstWorkingSegmentStartTime)) {
        offset = -differenceInMinutes(firstWorkingSegmentStartTime, actualStartTime);
      } else {
        const offsetForConsideration = differenceInMinutes(actualStartTime, firstWorkingSegmentStartTime);

        offset = offsetForConsideration > flexTimeExpectedEndTimeOffset
          ? flexTimeExpectedEndTimeOffset
          : offsetForConsideration;
      }
    } else {
      offset = flexTimeExpectedEndTimeOffset;
    }

    return offset;
  },

  getAdjustedFlextimeBandwidths(initialSegments, datePortion, offset) {
    return map(
      filter(initialSegments, (segment) => segment.category !== constants.WorkSchedule.SegmentType.FLEXTIME_BANDWIDTH),
      (segment) => {
        const adjustedSegmentStartTime = addMinutes(new Date(datePortion + segment.startTime), offset);
        const adjustedSegmentEndTime = addMinutes(new Date(datePortion + segment.endTime), offset);

        return {
          startTime: format(adjustedSegmentStartTime, this.HOURS_MINUTES_SECONDS_FORMAT),
          endTime: format(adjustedSegmentEndTime, this.HOURS_MINUTES_SECONDS_FORMAT),
          category: segment.category,
          minuteDuration: segment.minuteDuration,
        };
      },
    );
  },

  getAdjustedLeadingFlextimeBandwidth(
    startingBoundaryTime,
    adjustedFirstWorkingSegmentStartTime,
  ) {
    return {
      startTime: format(startingBoundaryTime, this.HOURS_MINUTES_SECONDS_FORMAT),
      endTime: format(adjustedFirstWorkingSegmentStartTime, this.HOURS_MINUTES_SECONDS_FORMAT),
      category: constants.WorkSchedule.SegmentType.FLEXTIME_BANDWIDTH,
      minuteDuration: differenceInMinutes(adjustedFirstWorkingSegmentStartTime, startingBoundaryTime),
    };
  },

  getAdjustedTrailingFlextimeBandwidth(adjustedLastWorkingSegmentEndTime, endingBoundaryTime) {
    return {
      startTime: format(adjustedLastWorkingSegmentEndTime, this.HOURS_MINUTES_SECONDS_FORMAT),
      endTime: format(endingBoundaryTime, this.HOURS_MINUTES_SECONDS_FORMAT),
      category: constants.WorkSchedule.SegmentType.FLEXTIME_BANDWIDTH,
      minuteDuration: differenceInMinutes(endingBoundaryTime, adjustedLastWorkingSegmentEndTime),
    };
  },

  getSegmentSummaries(segments, startTime, endTime, spanningDays) {
    return {
      segments: map(segments, (segment) => extend(
        {},
        segment,
        {
          widthPercentage: this.getSegmentWidthPercentage(segment.minuteDuration, spanningDays),
        },
      )),
      startTime,
      endTime,
    };
  },

  getSegmentWidthPercentage(minuteDuration, spanningDays) {
    return (minuteDuration / (spanningDays * constants.WorkSchedule.MINUTES_IN_DAY)) * 100;
  },

  getSpanningDays(timePair) {
    const startOfStartingDay = startOfDay(new Date(timePair.clockIn.date));
    const endTime = timePair.clockOut != null
      ? new Date(timePair.clockOut.durationAccurateDate)
      : addMinutes(new Date(), ((new Date()).getTimezoneOffset() - timePair.clockIn.timeZoneOffset));
    const endOfEndingDay = startOfDay(addDays(endTime, 1));

    return differenceInDays(endOfEndingDay, startOfStartingDay);
  },

  getExpectedSpanningDays(state, timePair, actualSpanningDays) {
    const workSchedule = timePair.workSchedule;
    if (!workSchedule) {
      return 0;
    }

    const days = timePair.workSchedule.days;
    const actualEndDay = (timePair.startingWorkScheduleDay + actualSpanningDays - 1) % days.length;

    const lastSegment = last(find(days, (workScheduleDay) => workScheduleDay.day === actualEndDay)?.segments);

    if (!lastSegment || !this.segmentIsAcrossMidnight(lastSegment)) {
      return actualSpanningDays;
    }

    return actualSpanningDays + 1;
  },

  segmentIsAcrossMidnight(segment) {
    const start = Number.parseInt(segment.startTime.slice(0, 2), 10);
    const end = Number.parseInt(segment.endTime.slice(0, 2), 10);
    return end < start;
  },

  doesWorkScheduleFilterApply(filterContainer, time) {
    switch (filterContainer) {
      case constants.WorkSchedule.Filters.UNDER_TIME:
        return time.workScheduleMinuteDeviation < 0;
      case constants.WorkSchedule.Filters.OVERTIME:
        return time.workScheduleMinuteDeviation > 0;
      default: return false;
    }
  },

  getTempWorkScheduleIdsForPendingAssignmentRequests(state) {
    return flatMap(
      map(
        state.subordinateEmployees,
        (subordinate) => flatMap(subordinate.subordinateAssignments, (assignment) => map(
          filter(
            assignment.tempWorkScheduleAssignmentRequests,
            (request) => request.ecSynchronisationStatus
                  === constants.EC_SYNCHRONISATION_STATUS.SYNCHRONISATION_PENDING,
          ),
          (request) => request.tempWorkScheduleExternalCode,
        )),
      ),
    );
  },

  getWorkScheduleColour(index) {
    const colours = constants.WorkSchedule.Colours;

    let variations = [];
    variations = variations.concat(
      times(colours.length, constant({ background: 'base', border: 'darken1' })),
    );
    variations = variations.concat(
      times(colours.length, constant({ background: 'lighten1', border: 'base' })),
    );
    variations = variations.concat(
      times(colours.length, constant({ background: 'lighten2', border: 'lighten1' })),
    );
    variations = variations.concat(
      times(colours.length, constant({ background: 'lighten3', border: 'lighten2' })),
    );

    const assignedColour = colours[index % colours.length];

    const colourVariation = variations[index % variations.length];

    return {
      background: assignedColour[colourVariation.background].shade,
      border: assignedColour[colourVariation.border].shade,
      text: assignedColour[colourVariation.background].textColour,
    };
  },
};

export default WorkScheduleUtil;
